import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import PriceInput from "../components/PriceInput";
import toast from "react-hot-toast";
import axios from "axios";
import { Plus, AlertTriangle, Package, Edit2, Camera, Printer, MessageCircle, Eye } from "lucide-react";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { useReactToPrint } from "react-to-print";
import { ProfessionalReceipt, shareViaWhatsApp } from "../components/receipts/ProfessionalReceipt";

const API = `${window.location.origin}/api`;

// أسباب التلف الشائعة
const damageReasons = [
  { value: "expired", labelAr: "انتهاء الصلاحية", labelEn: "Expired" },
  { value: "broken", labelAr: "كسر/تلف", labelEn: "Broken/Damaged" },
  { value: "spoiled", labelAr: "فساد", labelEn: "Spoiled" },
  { value: "lost", labelAr: "فقدان", labelEn: "Lost" },
  { value: "returned", labelAr: "مرتجع تالف", labelEn: "Damaged Return" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

export default function DamagesPage() {
  const { user } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [damages, setDamages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inputMode, setInputMode] = useState("select"); // select أو manual
  const [formData, setFormData] = useState({
    product_id: "",
    product_name: "",
    quantity: "",
    unit_price: "",
    reason: "",
    custom_reason: "",
  });
  const [isScannerOpen, setIsScannerOpen] = useState(false); // ماسح الباركود
  const [receiptData, setReceiptData] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const receiptRef = useRef();

  // معلومات المتجر للوصل
  const shopInfo = {
    name: user?.shop_name || "اسم المؤسسة",
    phone: user?.phone || "",
    address: ""
  };

  // طباعة الوصل
  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    documentTitle: `وصل_تالفات_${receiptData?.invoice_number || ""}`,
  });

  // فتح وصل التالف
  const openReceipt = (damage) => {
    const reasonObj = damageReasons.find(r => r.value === damage.reason);
    setReceiptData({
      ...damage,
      product_name: damage.product_name,
      quantity: damage.quantity,
      unit_price: damage.unit_price || (damage.value / damage.quantity),
      total_loss: damage.value,
      reason_text: reasonObj ? reasonObj.labelAr : damage.reason,
      custom_reason: damage.custom_reason || ""
    });
    setIsReceiptOpen(true);
  };

  // مشاركة عبر واتساب
  const handleShareWhatsApp = () => {
    if (receiptData) {
      shareViaWhatsApp("damage", receiptData, shopInfo);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [damagesRes, productsRes] = await Promise.all([
        axios.get(`${API}/damages`),
        axios.get(`${API}/products`),
      ]);
      setDamages(damagesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  // معالجة مسح الباركود للتالفات
  const handleDamageBarcodeScan = (barcode) => {
    const product = products.find(p => p.barcode === barcode || p.code === barcode);
    if (product) {
      setFormData({
        ...formData,
        product_id: product.id,
        product_name: product.name,
        unit_price: product.purchase_price?.toString() || product.cost_price?.toString() || "",
      });
      setInputMode("select");
      toast.success(
        language === "ar" 
          ? `✅ تم العثور على: ${product.name}` 
          : `✅ Found: ${product.name}`
      );
    } else {
      toast.error(
        language === "ar" 
          ? `❌ المنتج غير موجود في المخزون` 
          : `❌ Product not found in inventory`
      );
    }
    setIsScannerOpen(false);
  };

  // حساب القيمة التلقائي
  const calculateValue = () => {
    const qty = parseInt(formData.quantity) || 0;
    let price = 0;
    
    if (inputMode === "select" && formData.product_id) {
      const product = products.find(p => p.id === formData.product_id);
      price = product?.purchase_price || 0;
    } else if (inputMode === "manual") {
      price = parseFloat(formData.unit_price) || 0;
    }
    
    return qty * price;
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    setFormData({
      ...formData,
      product_id: productId,
      product_name: product?.name || "",
      unit_price: product?.purchase_price?.toString() || "",
    });
  };

  const handleSubmit = async () => {
    const productName = inputMode === "select" 
      ? products.find(p => p.id === formData.product_id)?.name 
      : formData.product_name;
    
    if (!productName) {
      toast.error(language === "ar" ? "أدخل اسم المنتج" : "Enter product name");
      return;
    }

    const reason = formData.reason === "other" ? formData.custom_reason : 
      damageReasons.find(r => r.value === formData.reason)?.[language === "ar" ? "labelAr" : "labelEn"] || formData.reason;

    try {
      await axios.post(`${API}/damages`, {
        product_id: inputMode === "select" ? formData.product_id : null,
        product_name: productName,
        quantity: parseInt(formData.quantity),
        reason: reason,
        value: calculateValue(),
      });
      toast.success(language === "ar" ? "تم التسجيل بنجاح" : "Recorded successfully");
      fetchData();
      handleCloseDialog();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || t("error"));
      } else {
        toast.error(t("error"));
      }
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setInputMode("select");
    setFormData({ product_id: "", product_name: "", quantity: "", unit_price: "", reason: "", custom_reason: "" });
  };

  const totalDamageValue = damages.reduce((sum, d) => sum + (d.value || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="damages-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">{t("nav.damages")}</h1>
          <p className="text-slate-400 mt-1">
            {language === "ar" ? `عدد التلفيات: ${damages.length}` : `Total Damages: ${damages.length}`}
          </p>
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="bg-red-600 hover:bg-red-700 btn-lift"
          data-testid="add-damage-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t("addDamage")}
        </Button>
      </div>

      {/* إجمالي التلفيات */}
      <Card className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-red-500/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-10 h-10 text-red-500" />
              <div>
                <p className="text-slate-400 text-sm">{language === "ar" ? "إجمالي قيمة التلفيات" : "Total Damage Value"}</p>
                <p className="text-3xl font-bold text-red-500">{formatCurrency(totalDamageValue)}</p>
                <USDHint amount={totalDamageValue} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : damages.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">{t("noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500">{language === "ar" ? "رقم الوصل" : "Invoice #"}</TableHead>
                    <TableHead className="text-amber-500">{t("date")}</TableHead>
                    <TableHead className="text-amber-500">{t("productName")}</TableHead>
                    <TableHead className="text-amber-500">{t("quantity")}</TableHead>
                    <TableHead className="text-amber-500">{t("damageReason")}</TableHead>
                    <TableHead className="text-amber-500">{t("value")}</TableHead>
                    <TableHead className="text-amber-500 text-center">{language === "ar" ? "الإجراءات" : "Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {damages.map((damage) => (
                    <TableRow key={damage.id} className="border-slate-700 hover:bg-slate-800/50">
                      <TableCell className="text-orange-400 font-mono font-bold">
                        {damage.invoice_number || "-"}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {formatDateTime(damage.created_at, language === "ar" ? "ar-IQ" : "en-US")}
                      </TableCell>
                      <TableCell className="text-white font-medium">{damage.product_name}</TableCell>
                      <TableCell className="text-slate-300">{damage.quantity}</TableCell>
                      <TableCell className="text-slate-300">{damage.reason}</TableCell>
                      <TableCell className="text-red-500 font-bold">
                        {formatCurrency(damage.value)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                            onClick={() => openReceipt(damage)}
                            title="عرض الوصل"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Damage Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{t("addDamage")}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* اختيار طريقة الإدخال */}
            <Tabs value={inputMode} onValueChange={setInputMode} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-slate-900">
                <TabsTrigger value="select" className="data-[state=active]:bg-red-600">
                  <Package className="w-4 h-4 me-2" />
                  {language === "ar" ? "اختيار من المخزون" : "Select from Inventory"}
                </TabsTrigger>
                <TabsTrigger value="manual" className="data-[state=active]:bg-red-600">
                  <Edit2 className="w-4 h-4 me-2" />
                  {language === "ar" ? "إدخال يدوي" : "Manual Entry"}
                </TabsTrigger>
              </TabsList>

              {/* اختيار من المخزون */}
              <TabsContent value="select" className="space-y-4 mt-4">
                {/* زر مسح الباركود */}
                <Button
                  type="button"
                  onClick={() => setIsScannerOpen(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Camera className="w-5 h-5 me-2" />
                  {language === "ar" ? "📷 مسح باركود المنتج التالف" : "📷 Scan Damaged Product"}
                </Button>
                
                <div className="space-y-2">
                  <Label className="text-slate-200">{t("productName")}</Label>
                  <Select
                    value={formData.product_id}
                    onValueChange={handleProductSelect}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="damage-product-select">
                      <SelectValue placeholder={language === "ar" ? "اختر المنتج" : "Select product"} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id} className="text-white hover:bg-slate-700">
                          {product.name} ({language === "ar" ? "الكمية:" : "Qty:"} {product.quantity})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.product_id && (
                  <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                    <p className="text-sm text-slate-400">{language === "ar" ? "سعر الوحدة:" : "Unit Price:"}</p>
                    <p className="text-lg font-bold text-amber-500">
                      {formatCurrency(products.find(p => p.id === formData.product_id)?.purchase_price || 0)}
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* إدخال يدوي */}
              <TabsContent value="manual" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-200">{t("productName")}</Label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder={language === "ar" ? "أدخل اسم المنتج" : "Enter product name"}
                    required={inputMode === "manual"}
                    data-testid="damage-product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-200">{language === "ar" ? "سعر الوحدة" : "Unit Price"}</Label>
                  <PriceInput
                    value={parseFloat(formData.unit_price) || 0}
                    onChange={(v) => setFormData({ ...formData, unit_price: String(v) })}
                    placeholder="0"
                    testId="damage-unit-price-input"
                    className="bg-slate-900 border-slate-700 text-white"
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* الحقول المشتركة */}
            <div className="space-y-2">
              <Label className="text-slate-200">{t("quantity")}</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="bg-slate-900 border-slate-700 text-white"
                required
                data-testid="damage-quantity-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-200">{t("damageReason")}</Label>
              <Select
                value={formData.reason}
                onValueChange={(value) => setFormData({ ...formData, reason: value })}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="damage-reason-select">
                  <SelectValue placeholder={language === "ar" ? "اختر السبب" : "Select reason"} />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {damageReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value} className="text-white hover:bg-slate-700">
                      {language === "ar" ? reason.labelAr : reason.labelEn}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.reason === "other" && (
              <div className="space-y-2">
                <Label className="text-slate-200">{language === "ar" ? "سبب آخر" : "Other Reason"}</Label>
                <Input
                  value={formData.custom_reason}
                  onChange={(e) => setFormData({ ...formData, custom_reason: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder={language === "ar" ? "أدخل السبب" : "Enter reason"}
                  required
                  data-testid="damage-custom-reason-input"
                />
              </div>
            )}

            {/* عرض القيمة المحسوبة */}
            {formData.quantity && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <p className="text-sm text-slate-400">{language === "ar" ? "قيمة التلف المحسوبة:" : "Calculated Damage Value:"}</p>
                <p className="text-2xl font-bold text-red-500">{formatCurrency(calculateValue())}</p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="border-slate-600 text-slate-300">
                {t("cancel")}
              </Button>
              <Button type="button" onClick={handleSubmit} className="bg-red-600 hover:bg-red-700" data-testid="save-damage-btn">
                {t("save")}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* ماسح الباركود للتالفات */}
      <BarcodeScanner
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScan={handleDamageBarcodeScan}
        language={language}
      />

      {/* Receipt Dialog - وصل التالفات */}
      <Dialog open={isReceiptOpen} onOpenChange={setIsReceiptOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              وصل التالفات
            </DialogTitle>
          </DialogHeader>
          
          {/* الوصل القابل للطباعة */}
          <div className="max-h-[60vh] overflow-y-auto">
            <ProfessionalReceipt
              ref={receiptRef}
              type="damage"
              data={receiptData || {}}
              shopInfo={shopInfo}
            />
          </div>

          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setIsReceiptOpen(false)}
              className="border-slate-600 text-slate-300"
            >
              إغلاق
            </Button>
            <Button
              onClick={handleShareWhatsApp}
              className="bg-green-600 hover:bg-green-700"
            >
              <MessageCircle className="w-4 h-4 me-2" />
              واتساب
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Printer className="w-4 h-4 me-2" />
              طباعة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
